import { useState, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Instagram, Mail, Phone, MessageCircle } from "lucide-react";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", subject: "", message: "", consent: true });
  const [loading, setLoading] = useState(false);
  const [settings, setSettings] = useState({});

  useEffect(() => { api.get("/site-settings").then((r) => setSettings(r.data)); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.consent) { toast.error("Please accept privacy consent."); return; }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Thank you — we've received your message.");
      setForm({ name: "", email: "", phone: "", subject: "", message: "", consent: true });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally { setLoading(false); }
  };

  return (
    <div data-testid="page-contact" className="bg-[#F9F8F6]">
      <section className="container-editorial pt-16 md:pt-24 pb-16">
        <div className="text-[10px] uppercase tracking-[0.32em] text-[#7A736C] mb-6">Contact</div>
        <h1 className="font-serif text-5xl md:text-7xl lg:text-[80px] tracking-tight leading-[1] max-w-4xl">Write to the studio.</h1>
        <p className="mt-8 max-w-2xl text-[#4A4642] leading-relaxed">For project enquiries, please use the consultation form. For everything else — press, collaborations, or a quiet hello — write to us here.</p>
      </section>

      <section className="container-editorial pb-24 grid grid-cols-1 md:grid-cols-12 gap-12 overflow-hidden">
        <div className="md:col-span-7">
          <form onSubmit={submit} data-testid="contact-form" className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              <Field label="Name" required><input required data-testid="contact-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" /></Field>
              <Field label="Email" required><input required type="email" data-testid="contact-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input" /></Field>
              <Field label="Phone"><input data-testid="contact-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input" /></Field>
              <Field label="Subject"><input data-testid="contact-subject" value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="input" /></Field>
            </div>
            <Field label="Message" required><textarea required rows={6} data-testid="contact-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="input" /></Field>
            <label className="flex items-start gap-3 text-sm text-[#4A4642]">
              <input type="checkbox" data-testid="contact-consent" checked={form.consent} onChange={(e) => setForm({ ...form, consent: e.target.checked })} className="mt-1" />
              <span>I agree to Hiraya storing my details to respond to my enquiry.</span>
            </label>
            <button type="submit" disabled={loading} data-testid="contact-submit" className="px-8 py-4 bg-[#2B2927] text-[#F9F8F6] text-xs uppercase tracking-[0.24em] rounded-sm hover:bg-[#A85C42] transition-colors disabled:opacity-60">
              {loading ? "Sending…" : "Send message"}
            </button>
          </form>
        </div>
        <div className="md:col-span-5 space-y-8 md:border-l md:border-[#D5D0C8] md:pl-12">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] text-[#7A736C] mb-3">Studio</div>
            <ul className="space-y-3 text-sm">
              {settings.email && <li className="flex items-center gap-3"><Mail size={14} /> <a className="hover:text-[#A85C42]" href={`mailto:${settings.email}`}>{settings.email}</a></li>}
              {settings.phone && <li className="flex items-center gap-3"><Phone size={14} /> <a className="hover:text-[#A85C42]" href={`tel:${settings.phone}`}>{settings.phone}</a></li>}
              {settings.whatsapp && <li className="flex items-center gap-3"><MessageCircle size={14} /> <a className="hover:text-[#A85C42]" href={`https://wa.me/${settings.whatsapp.replace(/\D/g, "")}`} target="_blank" rel="noreferrer">WhatsApp</a></li>}
              <li className="flex items-center gap-3"><Instagram size={14} /> <a href={settings.instagram || "https://www.instagram.com/thehiraya_"} target="_blank" rel="noreferrer" className="hover:text-[#A85C42]">@thehiraya_</a></li>
            </ul>
          </div>
          {settings.address && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#7A736C] mb-3">Address</div>
              <div className="text-sm leading-relaxed">{settings.address}</div>
            </div>
          )}
          {settings.service_areas?.length > 0 && (
            <div>
              <div className="text-[10px] uppercase tracking-[0.28em] text-[#7A736C] mb-3">Service Areas</div>
              <div className="text-sm">{settings.service_areas.join(" · ")}</div>
            </div>
          )}
        </div>
      </section>
      <style>{`.input{background:white;border:1px solid #D5D0C8;padding:14px 16px;width:100%;font-size:14px;border-radius:3px;outline:none;transition:border .2s}.input:focus{border-color:#A85C42}`}</style>
    </div>
  );
}
function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="block text-[10px] uppercase tracking-[0.28em] text-[#7A736C] mb-2">{label}{required && <span className="text-[#A85C42]"> *</span>}</span>
      {children}
    </label>
  );
}
